import React, { useState, useEffect } from 'react';
import { fetchItemLoss, saveItemLoss, deleteItemLoss } from '../../services/api';
import './ItemLoss.css';

const LOSS_TYPES = ['Evaporation', 'Testing', 'Leakage', 'Spillage', 'Other'];
const today = '04-12-2025';

export default function ItemLoss({ user }) {
  const companyId = user?.Companyid || user?.company_id || "1";

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const [form, setForm] = useState({
    date: formatDateForUI(new Date()),
    product: 'HSD',
    lossType: 'Evaporation',
    qty: '',
    rate: '',
    remarks: ''
  });

  useEffect(() => {
    loadData();
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await fetchItemLoss(companyId);
      if (res.success) {
        const raw = res.Response || [];
        const list = Array.isArray(raw) ? raw : [raw];
        setRecords(list.map(r => ({
          id: r.IDS || r.Recid,
          sno: r.SNo || r.Recid,
          date: r.Date1 || r.date,
          product: r.Product || r.product,
          lossType: r.LossType || r.lossType || r.Name,
          qty: parseFloat(r.Qty || r.qty || 0),
          rate: parseFloat(r.Rate || r.rate || 0),
          amount: parseFloat(r.Amount || r.amount || 0),
          remarks: r.Remarks || r.remarks || '—'
        })));
      }
    } catch (err) {
      console.error("ItemLoss fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  function formatDateForUI(d) {
    let month = '' + (d.getMonth() + 1);
    let day = '' + d.getDate();
    let year = d.getFullYear();
    if (month.length < 2) month = '0' + month;
    if (day.length < 2) day = '0' + day;
    return [year, month, day].join('-');
  }

  function formatDateForAPI(dateStr) {
    if (!dateStr || !dateStr.includes('-')) return dateStr;
    const [y, m, d] = dateStr.split('-');
    return `${d}-${m}-${y}`;
  }

  const handleSave = async () => {
    if (!form.qty || !form.rate) return;

    try {
      const payload = {
        Companyid: companyId,
        Date1: formatDateForAPI(form.date),
        Product: form.product,
        LossType: form.lossType,
        Qty: form.qty.toString(),
        Rate: form.rate.toString(),
        Amount: (parseFloat(form.qty) * parseFloat(form.rate)).toFixed(2),
        Remarks: form.remarks,
        IDS: "0"
      };

      const result = await saveItemLoss(payload);
      if (result.success || result.Response === "INSERTED") {
        await loadData();
        setShowForm(false);
        setForm({
          date: formatDateForUI(new Date()),
          product: 'HSD',
          lossType: 'Evaporation',
          qty: '',
          rate: '',
          remarks: ''
        });
      } else {
        alert(result.message || "Failed to save loss entry");
      }
    } catch (err) {
      console.error("ItemLoss save error:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this record?')) return;
    try {
      const result = await deleteItemLoss(id);
      if (result.success || result.Response === "DELETED" || result.status === "Success") {
        await loadData();
      } else {
        alert(result.message || "Failed to delete record");
      }
    } catch (err) {
      console.error("ItemLoss delete error:", err);
    }
  };

  const total = records.reduce((s, r) => s + (+r.amount || 0), 0);

  return (

    <div className="item-loss-page">

      <div className="page-header">

        <h2 className="page-title">Item Loss</h2>

        <button
          className="btn btn-primary btn-sm"
          onClick={() => setShowForm(!showForm)}
        >
          ＋ Add Loss Entry
        </button>

      </div>

      <div
        style={{
          background: '#fff',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '12px 16px',
          marginBottom: 14,
          borderLeft: '4px solid var(--danger)',
          display: 'inline-block'
        }}
      >

        <div
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: 'var(--danger)'
          }}
        >
          ₹{total.toFixed(2)}
        </div>

        <div
          style={{
            fontSize: 11.5,
            color: 'var(--text-secondary)'
          }}
        >
          Total Loss Value
        </div>

      </div>

      {showForm && (

        <div className="section-card">

          <div className="section-header">Add Item Loss</div>

          <div className="form-row">

            <div className="form-group">
              <label>Date</label>
              <input
                className="form-control"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Product</label>
              <select
                className="form-control"
                value={form.product}
                onChange={e => setForm({ ...form, product: e.target.value })}
              >
                <option>HSD</option>
                <option>MS</option>
                <option>CNG</option>
              </select>
            </div>

            <div className="form-group">
              <label>Loss Type</label>
              <select
                className="form-control"
                value={form.lossType}
                onChange={e => setForm({ ...form, lossType: e.target.value })}
              >
                {LOSS_TYPES.map(t => (
                  <option key={t}>{t}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Quantity (L)</label>
              <input
                type="number"
                className="form-control"
                value={form.qty}
                onChange={e => setForm({ ...form, qty: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Rate (₹)</label>
              <input
                type="number"
                className="form-control"
                value={form.rate}
                onChange={e => setForm({ ...form, rate: e.target.value })}
              />
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <input
                className="form-control"
                value={form.remarks}
                onChange={e => setForm({ ...form, remarks: e.target.value })}
              />
            </div>

          </div>

          <div className="action-bar" style={{ padding: '0 16px 14px' }}>

            <button className="btn btn-primary" onClick={handleSave}>
  <i className="bi bi-save me-1"></i> Save
</button>

            <button
              className="btn btn-secondary"
              onClick={() => setShowForm(false)}
            >
              ✕ Cancel
            </button>

          </div>

        </div>

      )}

      <div className="section-card">

        <div className="section-header">
          Item Loss Records — {records.length} records
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th className="text-center">Date</th>
                <th className="text-center">Product</th>
                <th className="text-left">Loss Type</th>
                <th className="text-right">Qty (L)</th>
                <th className="text-right">Rate (₹)</th>
                <th className="text-right">Amount (₹)</th>
                <th className="text-left">Remarks</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>
            <tbody>
              {records.map((r, i) => (
                <tr key={r.id}>
                  <td className="text-center">{r.date}</td>
                  <td className="text-center">
                    <span className={`badge ${r.product === 'MS' ? 'badge-danger' : r.product === 'CNG' ? 'badge-success' : 'badge-info'}`}>
                      {r.product}
                    </span>
                  </td>
                  <td className="text-left">{r.lossType}</td>
                  <td className="text-right">{parseFloat(r.qty || 0).toFixed(3)}</td>
                  <td className="text-right">₹{parseFloat(r.rate || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="text-right" style={{ color: 'var(--danger)', fontWeight: 700 }}>
                    ₹{parseFloat(r.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="text-left" style={{ fontSize: '0.85rem' }}>{r.remarks || '—'}</td>
                  <td className="text-center">
                    </td>
                </tr>
              ))}
            </tbody>
          </table>

        </div>

      </div>

    </div>

  );

}